<template>
  <div class="memorial-loading-page">
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="8" md="6" lg="4">
          <v-card class="loading-card" elevation="0">
            <v-card-text class="text-center pa-8">
              <div v-if="!error">
                <v-progress-circular
                  :size="70"
                  :width="7"
                  color="primary"
                  indeterminate
                  class="mb-6"
                ></v-progress-circular>
                <h2 class="text-h5 mb-3">Loading Memorial</h2>
                <p class="text-body-1 text--secondary">
                  Please wait while we retrieve your memorial...
                </p>
              </div>

              <div v-else>
                <v-icon color="error" size="70" class="mb-4">mdi-alert-circle</v-icon>
                <h2 class="text-h5 mb-3">Unable to Load Memorial</h2>
                <p class="text-body-1 text--secondary mb-6">
                  {{ errorMessage }}
                </p>
                <v-btn
                  color="primary"
                  variant="flat"
                  @click="goToProfile"
                  prepend-icon="mdi-arrow-left"
                >
                  Back to Profile
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'

const router = useRouter()
const route = useRoute()
const store = useMemorialStore()

const error = ref(false)
const errorMessage = ref('')

// Failsafe: if loading hasn't resolved (navigated away or shown an error) within this
// window, surface an error rather than leaving the spinner running indefinitely.
const LOADING_FAILSAFE_MS = 20000
let failsafeTimer = null

const showError = (message) => {
  if (failsafeTimer) {
    clearTimeout(failsafeTimer)
    failsafeTimer = null
  }
  error.value = true
  errorMessage.value = message
}

const loadMemorial = async () => {
  const memorialUuid = route.params.uuid

  if (!memorialUuid) {
    showError('No memorial ID provided')
    return
  }

  try {
    // Fetch memorial from API
    const response = await apiService.getMemorial(memorialUuid)

    if (!response.success || !response.memorial) {
      showError(response.msg || 'Memorial not found')
      return
    }

    const memorial = response.memorial

    // Load memorial data into store
    store.loadMemorialData(memorial)

    // Determine which page to navigate to based on memorial state
    if (failsafeTimer) {
      clearTimeout(failsafeTimer)
      failsafeTimer = null
    }
    if (!memorial.paymentCompleted) {
      // Payment pending - go to payment page
      router.push('/payment')
    } else {
      // Payment complete - go to memorial dashboard
      router.push('/memory/dashboard')
    }

  } catch (err) {
    console.error('Failed to load memorial:', err)
    showError('An error occurred while loading the memorial. Please try again.')
  }
}

const goToProfile = () => {
  router.push('/profile')
}

onMounted(() => {
  failsafeTimer = setTimeout(() => {
    showError('This is taking longer than expected. Please try again.')
  }, LOADING_FAILSAFE_MS)
  loadMemorial()
})

onBeforeUnmount(() => {
  if (failsafeTimer) {
    clearTimeout(failsafeTimer)
    failsafeTimer = null
  }
})
</script>

<style scoped>
.memorial-loading-page {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--v-theme-background) 0%, var(--v-theme-surface) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-card {
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-shadow: 0 24px 45px -20px rgba(15, 23, 42, 0.2);
  background: var(--v-theme-surface);
}
</style>
